import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { useHtmlIframeProps } from "@native-html/iframe-plugin";
import { useContext, useState, useEffect } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import { PlaceText } from "../../components/PlaceText";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DefaultShadow } from "../DefaultShadows";
import CheckBox from '@react-native-community/checkbox';
import { PlaceColors } from "../PlaceColors";
import { FindifyHTMLRender } from "../FindifyHTMLRender";
import Slider from '@react-native-community/slider';
import RadioGroup from 'react-native-radio-buttons-group';
import { Dropdown } from 'react-native-element-dropdown';
import Icon from "react-native-vector-icons/Ionicons";
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import { useNavigation } from "@react-navigation/native";
import { getTranslatedString } from "../FindifyTranslatedString";
import { getApiFromModule } from "../GetContentsByModule";


export const IframeRenderersProps = {
    scalesPageToFit: true,
    webViewProps: {}
}


export const FindifyFormHTMLRenderer = function FindifyFormHTMLRenderer(props: any) {
    return <FindifyFormBlock {...props} />
}


const submitForm = async (formId: any,
    form: any,
    formsApi: any,
    formData: any,
    findifyDataContext: any,
    navigation: any,
    setShowConfirmationMessage: any,
) => {
    const doSubmit = await form.trigger();

    if (!doSubmit)
        return;

    const formValues = form.getValues();
    const FormResponseList: any[] = [];

    formData?.questions?.forEach((questionData: any) => {
        var responseData = formValues[questionData?.form_question_id];
        const choicesData: any[] = [];

        if (!responseData)
            return;


        switch (questionData?.question_type) {
            case "multiple_choice":
            case "multiple_choice_other":
                const isOtherMC = questionData?.question_type == "multiple_choice_other";
                choicesData.push({
                    form_question_choice_id: responseData
                });

                if (isOtherMC && questionData.choices[questionData.choices.length - 1].form_question_choice_id == responseData) {
                    responseData = formValues[questionData?.form_question_id + '_other_value']
                }
                break;
            case "dropdown":
                responseData = responseData?.value;
                choicesData.push({
                    form_question_choice_id: responseData
                });
                break;
            case "checkboxes":
            case "checkboxes_other":
                const lastChoiceId = questionData.choices[questionData.choices.length - 1].form_question_choice_id;
                const isOtherCB = questionData?.question_type == "checkboxes_other";

                for (let i = 0; i < questionData.choices.length; i++) {
                    let choiceData = questionData.choices[i];
                    let responseDataValue = responseData[choiceData.form_question_choice_id]
                    const CheckboxChoiceFormResponse: any = {
                        form_question_id: questionData?.form_question_id,
                        language: findifyDataContext.language,
                        response: isOtherCB && responseDataValue && lastChoiceId == choiceData.form_question_choice_id ?
                            formValues[questionData?.form_question_id + "_other_value"] :
                            responseData[choiceData.form_question_choice_id],
                        choices: [
                            {
                                form_question_choice_id: choiceData.form_question_choice_id
                            }
                        ]
                    };

                    FormResponseList.push(CheckboxChoiceFormResponse);

                }
                return; // Don't duplicate response
            default: // DO nothing...
                break;

        }

        const FormResponse: any = {
            form_question_id: questionData?.form_question_id,
            language: findifyDataContext.language,
            response: responseData,
            choices: choicesData
        };

        FormResponseList.push(FormResponse);
    });


    const FormSubmissionData = {
        form_id: formId,
        language: findifyDataContext.language,
        question_responses: FormResponseList
    }

    // Submit
    formsApi.create(FormSubmissionData, null).then((data: any) => {
        confirmSubmission(formData, navigation, setShowConfirmationMessage);
    });
}


const confirmSubmission = (formData: any, navigation: any, setShowConfirmationMessage: Function) => {
    switch (formData.confirmation_type) {
        case "go_to_url":
            navigation.push('WebViewPage', { url: formData?.confirmation_value });
            break;
        case "show_page":
            navigation.push('PlaceContentDetails', {
                module: 'contents',
                content_id: formData.confirmation_value,
            });
            break;
        case "message":
        default:
            setShowConfirmationMessage(true);
            break;
    }
}

const FindifyFormBlock = ({ url, iframeProps, ...props }: any) => {
    const navigation = useNavigation();
    const formProps = useHtmlIframeProps(props);
    const findifyDataContext = useContext(FindifyDataContext);
    const colors = PlaceColors(findifyDataContext);
    const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
    const [formId, setFormId] = useState<any>();
    const [formData, setFormData] = useState<any>();
    const formsApi = getApiFromModule('forms', findifyDataContext);
    const { t } = useTranslation();
    const form = useForm({});

    useEffect(() => {
        if (formProps.htmlAttribs['data-lexical-form-id']) {
            const formId = formProps.htmlAttribs['data-lexical-form-id'];
            setFormId(formId);
            formsApi.get(formId, null).then((form: any) => {
                form?.questions?.sort((qA: any, qB: any) => { return qA.sequence - qB.sequence });  // Sort mutates array
                setFormData(form);
            })
        }
    }, []);

    if (formData) {
        if (showConfirmationMessage) {
            return <FindifyHTMLRender
                baseStyles={{ color: PlaceColors(findifyDataContext).text.get() }}
                htmlCode={getTranslatedString(formData.confirmation_message, findifyDataContext)} />
        }
        return <View style={{
            backgroundColor: colors.background.get(),
            borderRadius: 8,
            padding: 16,
        }}>
            <PlaceText style={{ marginBottom: 10, color: colors.primary.get(), fontSize: 20, fontWeight: '700' }}>
                {getTranslatedString(formData.name, findifyDataContext)}
            </PlaceText>
            <PlaceText style={{ marginBottom: 10 }}>
                {formData.description &&
                    <FindifyHTMLRender
                        baseStyles={{
                            color: PlaceColors(findifyDataContext).text.get()
                        }}
                        htmlCode={getTranslatedString(formData.description, findifyDataContext)} />
                }
            </PlaceText>
            {formData.questions?.map((question: any, qIndex: number) => {
                return <FindifyFormRendererQuestionInput
                    t={t}
                    findifyDataContext={findifyDataContext}
                    question={question}
                    qIndex={qIndex}
                    form={form}
                />
            })}
            <Pressable
                style={{
                    marginTop: 18,
                    minHeight: 48,
                    borderRadius: 6,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.primary.get(),
                }}
                onPress={() => submitForm(formId, form, formsApi, formData, findifyDataContext, navigation, setShowConfirmationMessage)}>
                <PlaceText style={{ color: colors.background.get(), fontWeight: '700', fontSize: 16 }}>
                    {getTranslatedString(formData.submit_button_text, findifyDataContext) !== '' ? getTranslatedString(formData.submit_button_text, findifyDataContext) : t('base.form.submit')}
                </PlaceText>
                {/* {submitButtonLabel && submitButtonLabel !== '' ? submitButtonLabel : t("forms_translations.save")} */}
            </Pressable>

        </View>;
    }
    return <></>;
}



export const FindifyFormRendererQuestionInput = ({
    t,
    findifyDataContext,
    question,
    qIndex,
    form,
}: {
    t: any;
    findifyDataContext: any;
    question: any;
    qIndex: number;
    form: any;
}) => {

    /* DO NOT DELETE */
    const {
        handleSubmit,
        control,
        reset,
        getValues,
        setValue,
        formState: { errors },
    } = form;
    /* DO NOT DELETE */
    const fieldKey = `${question?.form_question_id}`;
    const [showOther, setShowOther] = useState<any>();
    const questionType = question?.question_type;
    const colors = PlaceColors(findifyDataContext);
    const inputStyle = [{
        borderWidth: 1,
        borderRadius: 6,
        borderColor: colors.primary.withAlpha(90),
        backgroundColor: colors.background.solidAlphaWhiteBase(20),
        color: colors.text.get(),
        minHeight: 44,
        paddingHorizontal: 12,
        paddingVertical: 8,
    }, DefaultShadow];

    return <View style={{ marginTop: 14 }}>
        {/* QUESTION */}
        <PlaceText style={{ marginVertical: 8, color: colors.text.get(), fontSize: 16, fontWeight: '600' }}>{(qIndex + 1) + ". " + getTranslatedString(question?.question, findifyDataContext)}</PlaceText>
        <View>
            <Controller
                key={fieldKey}
                name={fieldKey}
                control={control}
                rules={{ required: question?.required }}
                render={({ field }) => {
                    switch (questionType) {
                        case "file_upload":
                            return <PlaceText id={fieldKey}
                            >TODO it does not work on site with mediamanagerpage</PlaceText>;
                            // <FindifyFormInput
                            //     disabled={false}
                            //     control={control}
                            //     errors={errors}
                            //     fieldKey={fieldKey ?? ''}
                            //     type={FormInputType.ModuleSelector}
                            //     moduleSelectorModule={ModuleSelectorInputType.MediaManager}
                            //     label={t("forms_translations.placeholder_select_file")}
                            // />
                            break;
                        case "stars":
                            const starsRatings = [
                                { value: 1 },
                                { value: 2 },
                                { value: 3 },
                                { value: 4 },
                                { value: 5 },
                            ];

                            return <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 5,
                                    justifyContent: 'center',

                                }}
                            >
                                {starsRatings?.map((v) => {
                                    return <Pressable onPress={() => {
                                        field.onChange(v.value)
                                    }}>
                                        <Icon name="star" size={36} color={field.value >= v.value ? colors.primary.get() : colors.primary.withAlpha(55)} />
                                    </Pressable>
                                })}
                            </View>;
                        case "faces":
                            const facesRatings = [
                                { value: 1, icon: "face-frown" },
                                { value: 2, icon: "face-frown-open" },
                                { value: 3, icon: "face-meh" },
                                { value: 4, icon: "face-smile" },
                                { value: 5, icon: "face-smile-beam" }
                            ];

                            return <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 5,
                                    justifyContent: 'center',
                                }}
                            >
                                {facesRatings?.map((f) => {
                                    return <Pressable onPress={() => {
                                        field.onChange(f.value)
                                    }}>
                                        <FontAwesome6 name={f.icon} size={36} color={field.value === f.value ? colors.primary.get() : colors.text.withAlpha(85)} />
                                    </Pressable>
                                })}
                            </View>;
                        case "slider":
                            return <Slider
                                id={fieldKey}
                                style={{ height: 40 }}
                                minimumValue={1}
                                maximumValue={5}
                                step={1}
                                minimumTrackTintColor={colors.primary.get()}
                                maximumTrackTintColor={colors.primary.withAlpha(70)}
                            />
                        case "text":
                            return <TextInput
                                style={inputStyle}
                                placeholderTextColor={colors.text.withAlpha(120)}
                                {...field}
                                onChangeText={field.onChange}
                            />
                        case "paragraph":
                            return <TextInput
                                {...field}
                                style={[inputStyle, { minHeight: 120, textAlignVertical: 'top' }]}
                                placeholderTextColor={colors.text.withAlpha(120)}
                                numberOfLines={6}
                                onChangeText={field.onChange}
                            />
                        case "multiple_choice":
                        case "multiple_choice_other":
                            const MCisOther = questionType === 'multiple_choice_other';
                            const multipleChoiceButtons = question.choices.map((choice: any) => {
                                return {
                                    label: getTranslatedString(choice.choice, findifyDataContext),
                                    value: choice.form_question_choice_id,
                                    id: choice.form_question_choice_id
                                }
                            });
                            return <>
                                <RadioGroup
                                    radioButtons={multipleChoiceButtons}
                                    onPress={field.onChange}
                                    selectedId={field.value}
                                />
                                {MCisOther && field?.value === multipleChoiceButtons[multipleChoiceButtons.length - 1]?.value &&
                                    <Controller
                                        key={fieldKey + '_other_value'}
                                        name={fieldKey + '_other_value'}
                                        control={control}
                                        rules={{ required: question?.required }}
                                        render={(fieldData) => {
                                            return <TextInput
                                                style={inputStyle}
                                                placeholderTextColor={colors.text.withAlpha(120)}
                                                {...fieldData.field}
                                                onChangeText={fieldData.field.onChange}
                                            />
                                        }}
                                    />
                                }
                            </>
                        case "dropdown":
                            const { ref, ...ddfield } = field;
                            return <Dropdown
                                {...ddfield}
                                style={{
                                    minHeight: 44,
                                    borderWidth: 1,
                                    borderRadius: 6,
                                    borderColor: colors.primary.withAlpha(90),
                                    backgroundColor: colors.background.solidAlphaWhiteBase(20),
                                    paddingHorizontal: 12,
                                }}
                                placeholderStyle={{ color: colors.text.withAlpha(130) }}
                                selectedTextStyle={{ color: colors.text.get() }}
                                itemTextStyle={{ color: colors.text.get() }}
                                containerStyle={{ backgroundColor: colors.background.get(), borderColor: colors.primary.withAlpha(90) }}
                                activeColor={colors.primary.withAlpha(35)}
                                data={question.choices}
                                search
                                maxHeight={300}
                                labelField={`choice.${findifyDataContext.language ?? ''}`}
                                valueField="form_question_choice_id"
                                // searchPlaceholder="Search..."
                                onChange={item => {
                                    field.onChange(item.form_question_choice_id);

                                }}
                            // renderLeftIcon={() => (

                            // )}
                            />;
                        case "checkboxes":
                        case "checkboxes_other":
                            const CBisOther = questionType === 'checkboxes_other';
                            const choices = question.choices;
                            return <>
                                {choices.map((choice: any) => {
                                    return <View
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 4,
                                        }}
                                    >
                                        <CheckBox
                                            // {...field}
                                            id={fieldKey + '.' + choice.form_question_choice_id}
                                            key={fieldKey + '.' + choice.form_question_choice_id}
                                            value={field.value && field.value[choice.form_question_choice_id]}
                                            onValueChange={(val) => {
                                                if (!field.value) {
                                                    field.value = {}
                                                }
                                                field.onChange({
                                                    ...field.value,
                                                    [choice.form_question_choice_id]: val
                                                })
                                            }}
                                            boxType="square"
                                            style={DefaultShadow}
                                            tintColor={colors.primary.withAlpha(120)}
                                            onTintColor={colors.primary.get()}
                                            onCheckColor={colors.background.get()}
                                        />
                                        <PlaceText style={{ color: colors.text.get() }}>{getTranslatedString(choice.choice, findifyDataContext)}</PlaceText>
                                    </View>

                                })}
                                {CBisOther && field?.value && field?.value[choices[choices.length - 1].form_question_choice_id] &&
                                    <Controller
                                        key={fieldKey + '_other_value'}
                                        name={fieldKey + '_other_value'}
                                        control={control}
                                        rules={{ required: question?.required }}
                                        render={({ field }) => {
                                            return <TextInput
                                                style={inputStyle}
                                                placeholderTextColor={colors.text.withAlpha(120)}
                                                {...field}
                                                onChangeText={field.onChange}
                                            />
                                        }}
                                    />
                                }

                            </>;
                        default:
                            return <PlaceText id={fieldKey}> TODO {questionType}</PlaceText>;
                    }
                }}
            />
        </View>
        {makeErrorLabel(errors, fieldKey, t, colors.primary.get())}
    </View>;
}



const makeErrorLabel = (errors: any, fieldKey: string, t: any, color: string = 'red') => {
    const keySteps = fieldKey.split('.');
    const errorType = keySteps.length === 1 ? errors[keySteps[0]]?.type : (keySteps.length === 2 && errors[keySteps[0]]) ? errors[keySteps[0]][keySteps[1]]?.type : null;
    let errorMsg: string | null = null;
    switch (errorType) {
        case 'required':
            errorMsg = t('base.form.errors.is_required');
            break;
        default:
            break;
    }
    if (errorMsg) {

        return <PlaceText
            style={{
                color,
                fontSize: 12,
                marginTop: 2,

            }}>
            {errorMsg}
        </PlaceText>
    }

    return <></>
} 
